import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogTrigger,
    DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { Download, Pencil, Upload, UserPlus, UsersRound } from "lucide-react";

export default function Admin() {
    const [users, setUsers] = useState([]);
    const [invitations, setInvitations] = useState([]);
    const [properties, setProperties] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [selectedPropertyIds, setSelectedPropertyIds] = useState([]);
    const [grouping, setGrouping] = useState(false);
    const [importOpen, setImportOpen] = useState(false);
    const [importRows, setImportRows] = useState("unit_number,parking_spaces\n1,P-RC01-A; P-RC01-B");
    const [importErrors, setImportErrors] = useState([]);
    const [editingProperty, setEditingProperty] = useState(null);
    const [propertyForm, setPropertyForm] = useState({
        unit_number: "",
        address: "",
        household_id: "",
        parking_spaces: "",
    });
    const [form, setForm] = useState({
        email: "",
        full_name: "",
        role: "resident",
        property_id: "",
        expires_in_days: 14,
    });

    const load = async () => {
        setLoading(true);
        try {
            const [u, p, invites] = await Promise.all([api.get("/admin/users"), api.get("/properties"), api.get("/admin/invitations")]);
            setUsers(u.data || []);
            setProperties(p.data || []);
            setInvitations(invites.data || []);
        } catch {
            toast.error("Failed to load admin data");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const invite = async (e) => {
        e.preventDefault();
        try {
            await api.post("/admin/users/invite", {
                email: form.email,
                full_name: form.full_name,
                role: form.role,
                property_id: form.property_id || null,
                expires_in_days: Number(form.expires_in_days || 14),
            });
            toast.success("Invitation created");
            setOpen(false);
            setForm({ email: "", full_name: "", role: "resident", property_id: "", expires_in_days: 14 });
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Invite failed");
        }
    };

    const inviteLink = (invite) => `${window.location.origin}/register?token=${encodeURIComponent(invite.token)}`;

    const copyInviteLink = async (invite) => {
        const link = inviteLink(invite);
        try {
            await navigator.clipboard.writeText(link);
            toast.success("Invitation link copied");
        } catch {
            toast.info(link);
        }
    };

    const revokeInvite = async (invite) => {
        if (!window.confirm(`Revoke invitation for ${invite.email}?`)) return;
        try {
            await api.post(`/admin/invitations/${invite.id}/revoke`);
            toast.success("Invitation revoked");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not revoke invitation");
        }
    };

    const queueInviteEmail = async (invite) => {
        try {
            await api.post(`/admin/invitations/${invite.id}/queue-email`);
            toast.success("Invitation email queued");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not queue invitation email");
        }
    };

    const propertyAddr = (id) => {
        const p = properties.find((pp) => pp.id === id);
        return p ? `${p.unit_number} - ${p.address}` : "-";
    };

    const propertyUsers = (propertyId) => users.filter((u) => u.property_id === propertyId);
    const householdCount = new Set(properties.map((p) => p.household_id).filter(Boolean)).size;
    const parkingConflicts = Object.values(properties.reduce((acc, property) => {
        (property.parking_spaces || []).forEach((space) => {
            const key = String(space || "").trim().toLowerCase();
            if (!key) return;
            acc[key] = acc[key] || { space, properties: [] };
            acc[key].properties.push(property);
        });
        return acc;
    }, {})).filter((entry) => entry.properties.length > 1);

    const toggleProperty = (propertyId) => {
        setSelectedPropertyIds((prev) => (
            prev.includes(propertyId) ? prev.filter((id) => id !== propertyId) : [...prev, propertyId]
        ));
    };

    const groupSelected = async () => {
        if (selectedPropertyIds.length < 1) {
            toast.error("Select at least one property");
            return;
        }
        setGrouping(true);
        try {
            await api.post("/admin/household-group", { property_ids: selectedPropertyIds });
            toast.success("Household grouping updated");
            setSelectedPropertyIds([]);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to group household");
        } finally {
            setGrouping(false);
        }
    };

    const openEditProperty = (property) => {
        setEditingProperty(property);
        setPropertyForm({
            unit_number: property.unit_number || "",
            address: property.address || "",
            household_id: property.household_id || "",
            parking_spaces: (property.parking_spaces || []).join("; "),
        });
    };

    const saveProperty = async (e) => {
        e.preventDefault();
        try {
            await api.patch(`/admin/properties/${editingProperty.id}`, {
                unit_number: propertyForm.unit_number,
                address: propertyForm.address,
                household_id: propertyForm.household_id || null,
                parking_spaces: propertyForm.parking_spaces.split(";").map((s) => s.trim()).filter(Boolean),
            });
            toast.success("Property updated");
            setEditingProperty(null);
            load();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            if (detail?.message === "Parking-space conflict") {
                const conflict = detail.conflicts?.[0];
                toast.error(`Parking conflict: ${conflict?.space || "space"} is already assigned to unit ${conflict?.unit_number || "another property"}`);
            } else {
                toast.error(typeof detail === "string" ? detail : "Failed to update property");
            }
        }
    };

    const exportProperties = async () => {
        try {
            const res = await api.get("/admin/properties/export", { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = "estate-properties.csv";
            a.click();
            URL.revokeObjectURL(url);
        } catch {
            toast.error("Failed to export properties");
        }
    };

    const importParkingSpaces = async (e) => {
        e.preventDefault();
        try {
            const res = await api.post("/admin/properties/parking-spaces/import", { rows: importRows });
            const updated = res.data?.updated?.length || 0;
            const errors = res.data?.errors?.length || 0;
            setImportErrors(res.data?.errors || []);
            toast.success(`Imported ${updated} properties${errors ? `, ${errors} errors` : ""}`);
            if (!errors) {
                setImportOpen(false);
            }
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Import failed");
        }
    };

    return (
        <div className="space-y-6" data-testid="admin-page">
            <div className="flex items-end justify-between gap-3 flex-wrap">
                <div>
                    <div className="text-xs uppercase tracking-widest text-slate-500">Admin</div>
                    <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">Users & properties</h1>
                    <p className="text-sm text-slate-500 mt-1">Invite users, assign properties, and maintain estate membership.</p>
                </div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button className="bg-slate-700 hover:bg-slate-800 text-white" data-testid="invite-user-button">
                            <UserPlus size={16} className="mr-2" /> Invite user
                        </Button>
                    </DialogTrigger>
                    <DialogContent data-testid="invite-dialog">
                        <DialogHeader>
                            <DialogTitle>Invite user</DialogTitle>
                            <DialogDescription>
                                Create a time-limited invitation link. The resident sets their own password when accepting.
                            </DialogDescription>
                        </DialogHeader>
                        <form onSubmit={invite} className="space-y-4">
                            <div className="space-y-1.5">
                                <Label>Full name</Label>
                                <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} required data-testid="invite-name-input" />
                            </div>
                            <div className="space-y-1.5">
                                <Label>Email</Label>
                                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="invite-email-input" />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div className="space-y-1.5">
                                    <Label>Role</Label>
                                    <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                                        <SelectTrigger data-testid="invite-role-select"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="resident">Resident</SelectItem>
                                            <SelectItem value="household_admin">Household admin</SelectItem>
                                            <SelectItem value="estate_admin">Estate admin</SelectItem>
                                            <SelectItem value="agency_viewer">Agency viewer</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-1.5">
                                    <Label>Property</Label>
                                    <Select value={form.property_id} onValueChange={(v) => setForm({ ...form, property_id: v })}>
                                        <SelectTrigger data-testid="invite-property-select"><SelectValue placeholder="Optional" /></SelectTrigger>
                                        <SelectContent>
                                            {properties.map((p) => (
                                                <SelectItem key={p.id} value={p.id}>{p.unit_number} - {p.address}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div className="space-y-1.5">
                                <Label>Expires in days</Label>
                                <Input type="number" min="1" max="90" value={form.expires_in_days} onChange={(e) => setForm({ ...form, expires_in_days: e.target.value })} required data-testid="invite-expiry-input" />
                            </div>
                            <DialogFooter>
                                <Button type="submit" className="bg-slate-700 hover:bg-slate-800 text-white" data-testid="submit-invite-button">Invite</Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            <section className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div className="border border-slate-200 rounded-lg bg-white p-4">
                    <div className="text-xs uppercase tracking-widest text-slate-500">Users</div>
                    <div className="font-heading text-2xl font-bold text-slate-900 mt-1">{users.length}</div>
                </div>
                <div className="border border-slate-200 rounded-lg bg-white p-4">
                    <div className="text-xs uppercase tracking-widest text-slate-500">Properties</div>
                    <div className="font-heading text-2xl font-bold text-slate-900 mt-1">{properties.length}</div>
                </div>
                <div className="border border-slate-200 rounded-lg bg-white p-4">
                    <div className="text-xs uppercase tracking-widest text-slate-500">Households</div>
                    <div className="font-heading text-2xl font-bold text-slate-900 mt-1">{householdCount}</div>
                </div>
                <div className="border border-slate-200 rounded-lg bg-white p-4">
                    <div className="text-xs uppercase tracking-widest text-slate-500">Parking spaces</div>
                    <div className="font-heading text-2xl font-bold text-slate-900 mt-1">
                        {properties.reduce((sum, p) => sum + (p.parking_spaces || []).length, 0)}
                    </div>
                </div>
            </section>

            {parkingConflicts.length > 0 && (
                <section className="border border-amber-200 rounded-lg bg-amber-50 p-4" data-testid="parking-conflicts-section">
                    <div className="text-xs uppercase tracking-widest text-amber-700">Parking-space conflicts</div>
                    <div className="mt-2 space-y-2">
                        {parkingConflicts.map((conflict) => (
                            <div key={conflict.space} className="text-sm text-amber-900" data-testid={`parking-conflict-${conflict.space}`}>
                                <span className="font-semibold">{conflict.space}</span> is assigned to{" "}
                                {conflict.properties.map((p) => `${p.unit_number} ${p.address}`).join(", ")}.
                            </div>
                        ))}
                    </div>
                </section>
            )}

            <section className="border border-slate-200 rounded-lg bg-white overflow-x-auto" data-testid="invitations-section">
                <div className="px-3 py-3 border-b border-slate-200">
                    <div className="text-xs uppercase tracking-widest text-slate-500">Invitations ({invitations.length})</div>
                    <p className="text-xs text-slate-500 mt-1">Pending invitation links expire automatically and can be revoked before acceptance.</p>
                </div>
                <table className="w-full text-sm" data-testid="invitations-table">
                    <thead>
                        <tr className="border-b border-slate-200 bg-slate-50">
                            <th className="table-header-cell">Resident</th>
                            <th className="table-header-cell">Role</th>
                            <th className="table-header-cell">Status</th>
                            <th className="table-header-cell">Email delivery</th>
                            <th className="table-header-cell">Expires</th>
                            <th className="table-header-cell"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {!loading && invitations.length === 0 && <tr><td colSpan={6} className="table-cell-tight text-slate-500">No invitations yet.</td></tr>}
                        {invitations.map((invite) => (
                            <tr key={invite.id} className="border-b border-slate-100" data-testid={`invitation-row-${invite.id}`}>
                                <td className="table-cell-tight">
                                    <div className="font-medium text-slate-900">{invite.full_name}</div>
                                    <div className="text-xs text-slate-500">{invite.email}</div>
                                </td>
                                <td className="table-cell-tight text-slate-700">{invite.role.replace("_", " ")}</td>
                                <td className="table-cell-tight text-slate-700">{invite.status}</td>
                                <td className="table-cell-tight text-slate-700">
                                    <div>{invite.delivery_status || "not queued"}</div>
                                    {invite.delivery_reason && (
                                        <div className="text-xs text-slate-500 max-w-xs truncate" title={invite.delivery_reason}>
                                            {invite.delivery_reason}
                                        </div>
                                    )}
                                </td>
                                <td className="table-cell-tight text-slate-500">{new Date(invite.expires_at).toLocaleDateString()}</td>
                                <td className="table-cell-tight">
                                    <div className="flex gap-2">
                                        {invite.status === "pending" && (
                                            <Button type="button" size="sm" variant="outline" onClick={() => queueInviteEmail(invite)} data-testid={`email-invite-${invite.id}`}>
                                                Email
                                            </Button>
                                        )}
                                        {invite.status === "pending" && (
                                            <Button type="button" size="sm" variant="outline" onClick={() => copyInviteLink(invite)} data-testid={`copy-invite-${invite.id}`}>
                                                Copy link
                                            </Button>
                                        )}
                                        {invite.status === "pending" && (
                                            <Button type="button" size="sm" variant="outline" onClick={() => revokeInvite(invite)} data-testid={`revoke-invite-${invite.id}`}>
                                                Revoke
                                            </Button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </section>

            <section className="border border-slate-200 rounded-lg bg-white overflow-x-auto">
                <table className="w-full text-sm" data-testid="users-table">
                    <thead>
                        <tr className="border-b border-slate-200 bg-slate-50">
                            <th className="table-header-cell">Name</th>
                            <th className="table-header-cell">Email</th>
                            <th className="table-header-cell">Role</th>
                            <th className="table-header-cell">Property</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && <tr><td colSpan={4} className="table-cell-tight text-slate-500">Loading...</td></tr>}
                        {!loading && users.map((u) => (
                            <tr key={u.id} className="border-b border-slate-100" data-testid={`user-row-${u.id}`}>
                                <td className="table-cell-tight font-medium text-slate-900">{u.full_name}</td>
                                <td className="table-cell-tight text-slate-700">{u.email}</td>
                                <td className="table-cell-tight text-slate-700">{u.role.replace("_", " ")}</td>
                                <td className="table-cell-tight text-slate-600">{propertyAddr(u.property_id)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </section>

            <section className="border border-slate-200 rounded-lg bg-white overflow-x-auto">
                <div className="px-3 py-3 border-b border-slate-200 flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <div className="text-xs uppercase tracking-widest text-slate-500">Properties ({properties.length})</div>
                        <p className="text-xs text-slate-500 mt-1">Select properties to group into one household, or edit parking allocations directly.</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={groupSelected}
                            disabled={grouping || selectedPropertyIds.length === 0}
                            data-testid="group-household-button"
                        >
                            <UsersRound size={15} className="mr-2" /> Group selected ({selectedPropertyIds.length})
                        </Button>
                        <Button type="button" variant="outline" onClick={exportProperties} data-testid="export-properties-button">
                            <Download size={15} className="mr-2" /> Export CSV
                        </Button>
                        <Dialog open={importOpen} onOpenChange={setImportOpen}>
                            <DialogTrigger asChild>
                                <Button type="button" variant="outline" data-testid="import-parking-button">
                                    <Upload size={15} className="mr-2" /> Import parking
                                </Button>
                            </DialogTrigger>
                            <DialogContent data-testid="parking-import-dialog">
                                <DialogHeader>
                                    <DialogTitle>Import parking spaces</DialogTitle>
                                    <DialogDescription>
                                        Paste CSV rows in the format unit_number,parking_spaces. Use semicolons between multiple spaces.
                                    </DialogDescription>
                                </DialogHeader>
                                <form onSubmit={importParkingSpaces} className="space-y-4">
                                    <Textarea rows={8} value={importRows} onChange={(e) => setImportRows(e.target.value)} data-testid="parking-import-rows" />
                                    {importErrors.length > 0 && (
                                        <div className="border border-amber-200 bg-amber-50 rounded-md p-3 space-y-2" data-testid="parking-import-errors">
                                            <div className="text-xs uppercase tracking-widest text-amber-700">Import errors</div>
                                            {importErrors.map((error, idx) => (
                                                <div key={`${error.line}-${idx}`} className="text-sm text-amber-900">
                                                    Line {error.line}: {error.error}
                                                    {error.conflicts?.length ? ` - ${error.conflicts.map((c) => `${c.space} on unit ${c.unit_number}`).join(", ")}` : ""}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    <DialogFooter>
                                        <Button type="submit" className="bg-slate-700 hover:bg-slate-800 text-white" data-testid="submit-parking-import">Import</Button>
                                    </DialogFooter>
                                </form>
                            </DialogContent>
                        </Dialog>
                    </div>
                </div>
                <table className="w-full text-sm" data-testid="properties-table">
                    <thead>
                        <tr className="border-b border-slate-200 bg-slate-50">
                            <th className="table-header-cell">Select</th>
                            <th className="table-header-cell">Unit</th>
                            <th className="table-header-cell">Address</th>
                            <th className="table-header-cell">Household</th>
                            <th className="table-header-cell">Residents</th>
                            <th className="table-header-cell">Parking spaces</th>
                            <th className="table-header-cell">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {properties.map((p) => (
                            <tr key={p.id} className="border-b border-slate-100" data-testid={`property-row-${p.unit_number}`}>
                                <td className="table-cell-tight">
                                    <input
                                        type="checkbox"
                                        checked={selectedPropertyIds.includes(p.id)}
                                        onChange={() => toggleProperty(p.id)}
                                        aria-label={`Select property ${p.unit_number}`}
                                        data-testid={`property-select-${p.unit_number}`}
                                    />
                                </td>
                                <td className="table-cell-tight font-medium text-slate-900">{p.unit_number}</td>
                                <td className="table-cell-tight text-slate-700">{p.address}</td>
                                <td className="table-cell-tight text-slate-500 font-mono text-xs">{(p.household_id || "-").slice(0, 8)}</td>
                                <td className="table-cell-tight text-slate-700">
                                    {propertyUsers(p.id).length ? propertyUsers(p.id).map((u) => (
                                        <div key={u.id}>
                                            {u.full_name}
                                            <span className="text-xs text-slate-500"> ({u.role.replace("_", " ")})</span>
                                        </div>
                                    )) : "-"}
                                </td>
                                <td className="table-cell-tight text-slate-700">{(p.parking_spaces || []).join(", ") || "-"}</td>
                                <td className="table-cell-tight">
                                    <Button type="button" variant="outline" size="sm" onClick={() => openEditProperty(p)} data-testid={`edit-property-${p.unit_number}`}>
                                        <Pencil size={14} className="mr-1" /> Edit
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </section>

            <Dialog open={Boolean(editingProperty)} onOpenChange={(v) => !v && setEditingProperty(null)}>
                <DialogContent data-testid="edit-property-dialog">
                    <DialogHeader>
                        <DialogTitle>Edit property</DialogTitle>
                        <DialogDescription>
                            Keep household membership and allocated parking spaces aligned with the estate register.
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={saveProperty} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label>Unit</Label>
                                <Input value={propertyForm.unit_number} onChange={(e) => setPropertyForm({ ...propertyForm, unit_number: e.target.value })} required data-testid="edit-property-unit" />
                            </div>
                            <div className="space-y-1.5">
                                <Label>Household ID</Label>
                                <Input value={propertyForm.household_id} onChange={(e) => setPropertyForm({ ...propertyForm, household_id: e.target.value })} data-testid="edit-property-household" />
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <Label>Address</Label>
                            <Input value={propertyForm.address} onChange={(e) => setPropertyForm({ ...propertyForm, address: e.target.value })} required data-testid="edit-property-address" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Parking spaces</Label>
                            <Textarea rows={3} value={propertyForm.parking_spaces} onChange={(e) => setPropertyForm({ ...propertyForm, parking_spaces: e.target.value })} placeholder="P-RC01-A; P-RC01-B" data-testid="edit-property-parking" />
                        </div>
                        <DialogFooter>
                            <Button type="submit" className="bg-slate-700 hover:bg-slate-800 text-white" data-testid="save-property-button">Save property</Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
